import React from 'react';
import Section from '../Section';
import BannerSectionStyle4 from '../Section/BannerSection/BannerSectionStyle4';
import { pageTitle } from '../../helpers/PageTitle';

const contexts = [
  { title: 'Voyage d’affaires', text: 'Un créneau rapide, sans faire dérailler votre agenda parisien.' },
  { title: 'Vacances', text: 'Un bilan clair, pour reprendre votre séjour l’esprit libre.' },
  { title: 'De passage', text: 'La majorité des examens, ici, au XVIᵉ — pas de parcours éparpillé.' },
];

const examData = [
  {
    title: 'Électrocardiogramme (ECG)',
    iconUrl: '/images/home_1/exam_ecg.svg',
  },
  {
    title: 'Échographie cardiaque (ETT)',
    iconUrl: '/images/home_1/exam_echo.svg',
  },
  {
    title: 'Holter ECG',
    iconUrl: '/images/home_1/exam_holter_ecg.svg',
  },
  {
    title: 'Holter tensionnel (MAPA)',
    iconUrl: '/images/home_1/exam_mapa.svg',
  },
  {
    title: 'Épreuve d’effort',
    iconUrl: '/images/home_1/exam_effort.svg',
  },
  {
    title: 'Échographie cardiaque d’effort',
    iconUrl: '/images/home_1/exam_echo_effort.svg',
  },
  {
    title: 'Polygraphie du sommeil',
    iconUrl: '/images/home_1/exam_polygraphie.svg',
  },
  {
    title: 'Spirométrie',
    iconUrl: '/images/home_1/exam_spirometrie.svg',
  },
  {
    title: 'Score calcique',
    iconUrl: '/images/home_1/exam_score_calcique.svg',
  },
];

const reasons = [
  'Rendez-vous rapides',
  'Cabinet moderne au cœur de Paris',
  'Tous les examens cardiovasculaires sur place',
  'Cardiologue expérimenté',
  'Prise en charge personnalisée',
  'Suivi médical si nécessaire',
];

export default function InternationalPatients() {
  pageTitle('Patients internationaux');
  return (
    <>
      <section className="cs_corporate_hero">
        <div
          className="cs_corporate_hero_bg cs_bg_filed"
          style={{ backgroundImage: 'url(/images/home_1/hero_bg.jpeg)' }}
        />
        <div className="container">
          <div className="cs_corporate_hero_grid">
            <div>
              <p className="cs_corporate_kicker">International patients · FR / EN</p>
              <h1 className="cs_corporate_hero_title">
                Patients
                <br />
                internationaux
              </h1>
              <p className="cs_corporate_hero_lead">
                Votre santé cardiovasculaire, même loin de chez vous.
              </p>
              <div className="cs_intl_langs">
                <span>Français</span>
                <span>English</span>
              </div>
              <a
                href="https://www.doctolib.fr/cabinet-medical/paris/icvt"
                className="cs_btn cs_style_1"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Prendre rendez-vous</span>
                <i>
                  <img src="/images/icons/arrow_white.svg" alt="" />
                  <img src="/images/icons/arrow_white.svg" alt="" />
                </i>
              </a>
            </div>
            <aside className="cs_corporate_quote">
              <span className="cs_corporate_quote_mark" aria-hidden="true">
                “
              </span>
              <p>
                Nous savons qu’un problème de santé pendant un voyage nécessite
                une réponse efficace.
              </p>
              <small>16ᵉ arrondissement · Paris</small>
            </aside>
          </div>
        </div>
      </section>

      <Section topMd={140} topLg={100} topXl={80} bottomMd={40}>
        <div className="container">
          <p className="cs_corporate_intro">
            Que vous soyez en voyage d’affaires, en vacances ou simplement de
            passage à Paris, l’Institut Cardiovasculaire Trocadéro (ICVT) vous
            accueille pour une prise en charge cardiologique rapide, complète et
            personnalisée.
          </p>
          <div className="cs_intl_contexts">
            {contexts.map((item) => (
              <article key={item.title} className="cs_corporate_risk">
                <span>ICVT</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </Section>

      <Section bottomMd={80}>
        <div className="container">
          <div className="cs_corporate_offer">
            <div className="cs_corporate_offer_media">
              <img
                src="/images/about/docteur-issa.png"
                alt="Institut Cardiovasculaire Trocadéro, Paris 16e"
              />
            </div>
            <div className="cs_corporate_offer_body">
              <p className="cs_corporate_kicker">Paris 16ᵉ</p>
              <h2 className="cs_fs_48 cs_semibold">
                Un plateau technique, sur place
              </h2>
              <p>
                Situé au cœur du 16ᵉ arrondissement de Paris, notre cabinet met
                à votre disposition un plateau technique moderne permettant de
                réaliser la majorité des examens cardiovasculaires sur place,
                dans un environnement confortable et accueillant.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section bottomMd={80}>
        <div className="container">
          <div className="cs_corporate_lab">
            <div className="cs_corporate_lab_stamp">24h</div>
            <div>
              <h2 className="cs_fs_32 cs_semibold mb-3">
                Une prise en charge rapide
              </h2>
              <p>
                Nous savons qu’un problème de santé pendant un voyage nécessite
                une réponse efficace.
              </p>
              <p className="mb-0">
                Notre équipe s’engage à proposer des rendez-vous dans les
                meilleurs délais afin de limiter toute interruption de votre
                séjour.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section
        topMd={40}
        bottomMd={80}
        className="cs_corporate_exams_band"
      >
        <div className="container">
          <div className="cs_section_heading cs_style_1">
            <h3 className="cs_section_subtitle text-uppercase cs_accent_color cs_semibold m-0 cs_fs_20">
              Bilan
            </h3>
            <div className="cs_height_10" />
            <h2 className="cs_section_title cs_fs_48 m-0">
              Un bilan cardiovasculaire complet
            </h2>
            <p className="cs_corporate_intro cs_corporate_intro_left">
              Selon votre situation, nous réalisons notamment :
            </p>
          </div>
          <div className="cs_corporate_exams">
            {examData.map((item) => (
              <article key={item.title} className="cs_corporate_exam">
                <img src={item.iconUrl} alt="" />
                <h3>{item.title}</h3>
              </article>
            ))}
          </div>
        </div>
      </Section>

      <Section bottomMd={80}>
        <div className="container">
          <div className="cs_corporate_follow">
            <div>
              <p className="cs_corporate_kicker">Voyageurs</p>
              <h2 className="cs_fs_48 cs_semibold">
                Une prise en charge adaptée aux voyageurs internationaux
              </h2>
              <p>
                Nous accueillons des patients de toutes nationalités et prenons
                le temps d’expliquer chaque étape du bilan afin de garantir une
                prise en charge claire, rassurante et personnalisée.
              </p>
              <p>
                À l’issue des examens, le cardiologue vous remet ses conclusions
                ainsi que les résultats nécessaires pour assurer la continuité
                de votre suivi médical dans votre pays de résidence si besoin.
              </p>
            </div>
            <div className="cs_intl_passport">
              <strong>Continuité des soins</strong>
              <p>
                Compte-rendu et résultats à emporter, pour votre cardiologue
                dans votre pays de résidence.
              </p>
              <ul>
                <li>Conclusions du cardiologue</li>
                <li>Résultats des examens</li>
                <li>Suivi possible à distance si nécessaire</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <Section bottomMd={80}>
        <div className="container">
          <div className="cs_section_heading cs_style_1 text-center">
            <h3 className="cs_section_subtitle text-uppercase cs_accent_color cs_semibold m-0 cs_fs_20">
              ICVT
            </h3>
            <div className="cs_height_10" />
            <h2 className="cs_section_title cs_fs_48 m-0">
              Pourquoi choisir l’ICVT ?
            </h2>
          </div>
          <div className="cs_height_50" />
          <div className="cs_intl_reasons">
            {reasons.map((item, index) => (
              <article key={item} className="cs_intl_reason">
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item}</h3>
              </article>
            ))}
          </div>
        </div>
      </Section>

      <Section id="rdv" className="cs_footer_margin_0" bottomMd={0}>
        <BannerSectionStyle4
          bgUrl="/images/about/banner_bg_2.jpeg"
          title="Besoin d’un rendez-vous pendant votre séjour ?"
          subTitle="Une prise en charge cardiologique rapide, complète et personnalisée, au cœur du 16ᵉ arrondissement."
          center
        />
        <div className="container">
          <div className="cs_corporate_cta_actions">
            <a
              href="https://www.doctolib.fr/cabinet-medical/paris/icvt"
              className="cs_btn cs_style_1"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>Réserver sur Doctolib</span>
              <i>
                <img src="/images/icons/arrow_white.svg" alt="" />
                <img src="/images/icons/arrow_white.svg" alt="" />
              </i>
            </a>
            <a href="tel:+33156910846" className="cs_btn cs_style_1 cs_btn_white">
              <span>+33 01 56 91 08 46</span>
              <i>
                <img src="/images/icons/arrow_white.svg" alt="" />
                <img src="/images/icons/arrow_white.svg" alt="" />
              </i>
            </a>
          </div>
        </div>
      </Section>
    </>
  );
}
